import { apiFetch, apiUpload } from './api';

const PENDING_CART_KEY = 'kio_pending_cart_id';
const SATIM_ORDER_KEY = 'kio_satim_order_id';
const CHECKOUT_CONTEXT_KEY = 'kio_checkout_context';
const SATIM_ORDER_PARAMS = ['orderId', 'order_id', 'mdOrder', 'OrderId'];

async function readApiError(res, fallback) {
  let msg = fallback;
  try {
    const data = await res.json();
    if (typeof data === 'object' && data !== null) {
      if (data.detail) return String(data.detail);
      if (data.message) return String(data.message);
      if (data.error) return String(data.error);
      const parts = Object.entries(data).map(([key, value]) => {
        const text = Array.isArray(value) ? value.join(', ') : String(value);
        return `${key}: ${text}`;
      });
      if (parts.length) return parts.join(' · ');
    }
  } catch {
    // keep default
  }
  return msg;
}

function fileUrlFromUpload(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  return String(data.file ?? data.src ?? data.url ?? data.path ?? '');
}

function pickCartId(data) {
  if (!data) return null;
  return data.id ?? data.cart_id ?? data.pk ?? data.uuid ?? null;
}

function pickPaymentRedirect(data) {
  if (!data) return null;
  const paymentInfo = data.payment_info ?? data.paymentInfo ?? null;
  const results = data.results ?? paymentInfo?.results ?? null;
  // SATIM / CIB gateway: URL is nested inside payment_info.results.formUrl
  const fromResults = results?.formUrl ?? results?.form_url ?? null;
  if (fromResults) return fromResults;
  return paymentInfo?.formUrl
    ?? paymentInfo?.form_url
    ?? data.url
    ?? data.redirect_url
    ?? data.payment_url
    ?? data.gateway_url
    ?? data.satim_url
    ?? data.formUrl
    ?? data.form_url
    ?? null;
}

export function storePendingCartId(cartId) {
  if (cartId) sessionStorage.setItem(PENDING_CART_KEY, String(cartId));
}

export function getPendingCartId() {
  return sessionStorage.getItem(PENDING_CART_KEY);
}

export function clearPendingCartId() {
  sessionStorage.removeItem(PENDING_CART_KEY);
}

/** SATIM order id from return URL only — never sessionStorage (avoids false verify before payment). */
export function getSatimOrderIdFromSearch(search = '') {
  const params = new URLSearchParams(search);
  for (const key of SATIM_ORDER_PARAMS) {
    const value = params.get(key);
    if (value) return value;
  }
  return null;
}

export function clearSatimOrderId() {
  sessionStorage.removeItem(SATIM_ORDER_KEY);
}

export function storeCheckoutContext(context) {
  if (!context) return;
  sessionStorage.setItem(CHECKOUT_CONTEXT_KEY, JSON.stringify(context));
}

export function getCheckoutContext() {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_CONTEXT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearCheckoutContext() {
  sessionStorage.removeItem(CHECKOUT_CONTEXT_KEY);
}

export function buildCheckoutSearchParams(context = {}) {
  const params = new URLSearchParams();
  if (context.type) params.set('type', context.type);
  if (context.pack) params.set('pack', context.pack);
  if (context.domain) params.set('domain', context.domain);
  if (context.currency) params.set('currency', context.currency);
  if (context.annual === true || context.annual === 'true') params.set('annual', 'true');
  if (context.price != null && context.price !== '') params.set('price', String(context.price));
  return params;
}

/** KioStore pricing tab — used for checkout back / success navigation. */
export const CHECKOUT_PRICING_URL = '/channels/kiostore?tab=plan';

/** Return path for SATIM after CIB payment (new admin checkout verification step). */
export function buildCheckoutReturnUrl(context = {}) {
  const params = buildCheckoutSearchParams(context);
  const query = params.toString();
  return query ? `/checkout?${query}` : '/checkout';
}

export function stripSatimParamsFromSearch(search = '') {
  const params = new URLSearchParams(search);
  SATIM_ORDER_PARAMS.forEach((key) => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : '';
}

function readServicePrice(value, currency = 'DZD') {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'object') {
    const amount = value[currency] ?? value.DZD ?? value.amount;
    return amount == null ? null : Number(amount);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Extrait le montant réduit renvoyé par POST /services/payments/reduction/. */
export function parseReductionAmount(result, { currency = 'DZD' } = {}) {
  if (!result || typeof result !== 'object') return null;
  const candidates = [
    result.price,
    result.amount,
    result.total,
    result.final_price,
    result.finalPrice,
    result.service?.price,
    result.service?.amount,
    result.service?.total,
    result.calculated_pricing?.normal,
    result.payment?.amount,
  ];
  for (const candidate of candidates) {
    const value = readServicePrice(candidate, currency);
    if (value != null) return value;
  }
  return null;
}

/** POST /services/payments/reduction/?format=json */
export async function validateCheckoutReduction({ code, packId, currency = 'DZD' }) {
  const res = await apiFetch('/services/payments/reduction/?format=json', {
    method: 'POST',
    body: JSON.stringify({
      reduction: code,
      service: { pack: packId, currency },
    }),
  });
  if (!res.ok) throw new Error(await readApiError(res, 'Code promo invalide'));
  try { return await res.json(); } catch { return { reduction: code }; }
}

async function readResponseBody(res) {
  try {
    const text = await res.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** POST /services/files/?format=json — preuve de paiement reçu */
export async function uploadServiceReceipt(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiUpload('/services/files/?format=json', formData);
  if (!res.ok) throw new Error(await readApiError(res, 'Impossible de télécharger le reçu'));
  const data = await readResponseBody(res);
  const src = fileUrlFromUpload(data);
  if (!src) throw new Error('Réponse upload invalide');
  return src;
}

/** POST /services/carts/?format=json */
export async function createPackCart({
  paymentMethod,
  packId,
  currency = 'DZD',
  returnUrl = '/checkout',
  reduction = null,
  receiptUrl = '',
  note = '',
}) {
  const body = {
    payment_method: paymentMethod,
    payment_info: { file: receiptUrl || '', file2: null },
    note: note || 'checkout pack',
    items: { pack: packId, currency, returnUrl },
    reduction: paymentMethod === 'CIB' ? (reduction || null) : (reduction ?? ''),
  };

  const res = await apiFetch('/services/carts/?format=json', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readApiError(res, 'Impossible de créer le panier'));

  const data = await readResponseBody(res);
  const cartId = pickCartId(data);
  const isReceipt = paymentMethod === 'RECU';
  const paymentResults = data?.payment_info?.results ?? data?.results ?? null;
  const satimOrderId = paymentResults?.orderId ?? paymentResults?.order_id ?? null;

  if (!isReceipt && cartId) storePendingCartId(cartId);

  return {
    data,
    cartId,
    satimOrderId,
    status: res.status,
    isReceiptPending: isReceipt && res.status >= 200 && res.status < 300,
    redirectUrl: isReceipt ? null : pickPaymentRedirect(data),
  };
}

/** POST /services/carts/domain/?format=json */
export async function createDomainCart({
  domain,
  storeSlug,
  currency = 'DZD',
  returnUrl = '/checkout',
  reduction = null,
  note = '',
}) {
  const body = {
    payment_method: 'CIB',
    note: note || 'checkout domain',
    reduction,
    items: {
      domain,
      currency,
      store: storeSlug,
      returnUrl,
    },
  };

  const res = await apiFetch('/services/carts/domain/?format=json', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readApiError(res, 'Impossible de créer le panier domaine'));
  const data = await readResponseBody(res);
  const cartId = pickCartId(data);
  if (cartId) storePendingCartId(cartId);
  return { data, cartId, redirectUrl: pickPaymentRedirect(data) };
}

/** POST /services/carts/{cartId}/check-payment/?format=json */
export async function checkServicePayment(cartId, orderId) {
  const res = await apiFetch(`/services/carts/${cartId}/check-payment/?format=json`, {
    method: 'POST',
    body: JSON.stringify({ order_id: orderId }),
  });
  if (!res.ok) throw new Error(await readApiError(res, 'Impossible de vérifier le paiement'));
  try { return await res.json(); } catch { return null; }
}

/** GET /services/cart-payment/{cartId}/ — invoice data after successful CIB payment */
export async function fetchCartPaymentOrder(cartId) {
  const res = await apiFetch(`/services/cart-payment/${cartId}/?format=json`);
  if (!res.ok) throw new Error(await readApiError(res, 'Impossible de charger la facture'));
  try {
    const data = await res.json();
    return data?.data ?? data;
  } catch {
    return null;
  }
}

/** POST /services/checkout-mails/ — email receipt with PDF attachment */
export async function sendCheckoutReceiptEmail({ file, email }) {
  const paths = ['/services/checkout-mails/?format=json', '/services/checkout-mails/'];
  let lastError = 'Impossible d\'envoyer le reçu';
  for (const path of paths) {
    const body = new FormData();
    body.append('email', email);
    body.append('subject', 'Reçu de paiement');
    body.append(
      'message',
      'Nous vous remercions pour votre achat de pack. Vous trouverez ci-joint votre facture acquittee',
    );
    body.append('attach', file, file.name || 'invoice.pdf');
    const res = await apiUpload(path, body);
    if (res.ok && (res.status === 200 || res.status === 201)) return 'success';
    lastError = await readApiError(res, lastError);
  }
  throw new Error(lastError);
}

export function pickPaymentDetails(result) {
  if (!result) return null;
  return result.Details
    ?? result.details
    ?? result.payment_info?.results
    ?? result.results
    ?? result;
}

/** SATIM deposit amount is in centimes — same formatting as neo-store-dashboard */
export function formatCibDepositAmount(depositAmount, currency = 'DZD') {
  if (depositAmount == null || depositAmount === '') return '—';
  const raw = String(depositAmount);
  if (!/^\d+$/.test(raw)) {
    const n = Number(depositAmount);
    return Number.isFinite(n) ? `${n.toLocaleString('en-US')} ${currency}` : '—';
  }
  if (raw.length <= 2) return `0.${raw.padStart(2, '0')} ${currency}`;
  const formatted = `${raw.slice(0, -2)}.${raw.slice(-2)}`;
  const n = Number(formatted);
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function isCibDetailsSuccessful(details) {
  if (!details) return false;
  const respCode = details?.params?.respCode;
  const errorCode = String(details?.ErrorCode ?? details?.errorCode ?? '');
  const orderStatus = Number(details?.OrderStatus ?? details?.orderStatus);
  return respCode === '00'
    && (errorCode === '0' || errorCode === '2')
    && orderStatus === 2;
}

export function buildCibSuccessInfos(details, orderId, t) {
  if (!isCibDetailsSuccessful(details)) return [];
  const dateOpts = {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  };
  return [
    { label: t.checkout_cib_detail_description, value: details?.params?.respCode_desc || '—' },
    { label: t.checkout_cib_detail_satim_id, value: orderId || '—' },
    { label: t.checkout_cib_detail_order, value: details?.OrderNumber ?? '—' },
    { label: t.checkout_cib_detail_auth, value: details?.approvalCode ?? '—' },
    {
      label: t.checkout_cib_detail_amount,
      value: formatCibDepositAmount(details?.depositAmount ?? details?.Amount),
    },
    { label: t.checkout_cib_detail_method, value: t.checkout_cib_card },
    { label: t.checkout_cib_detail_date, value: new Date().toLocaleDateString('fr-FR', dateOpts) },
  ];
}

export function formatPaymentDetails(result) {
  const details = pickPaymentDetails(result) ?? {};
  const amountRaw = details.depositAmount ?? details.Amount ?? details.amount ?? null;
  return {
    amount: amountRaw != null ? Number(amountRaw) : null,
    amountLabel: formatCibDepositAmount(amountRaw),
    orderNumber: details.OrderNumber ?? details.orderNumber ?? details.orderId ?? details.order_id ?? null,
    authorization: details.approvalCode ?? details.ApprovalCode ?? details.authCode ?? details.authorizationCode ?? null,
    date: details.authDateTime ?? details.AuthDateTime ?? details.date ?? details.paymentDate ?? null,
    message: paymentResultMessage(result),
  };
}

export function isPaymentSuccessful(result) {
  return isCibDetailsSuccessful(pickPaymentDetails(result));
}

export function paymentResultMessage(result, t) {
  const details = pickPaymentDetails(result) ?? {};
  if (isCibDetailsSuccessful(details)) {
    return details?.params?.respCode_desc
      || details?.actionCodeDescription
      || 'Paiement confirmé';
  }
  const respCode = details?.params?.respCode;
  const errorCode = String(details?.ErrorCode ?? details?.errorCode ?? '');
  const orderStatus = Number(details?.OrderStatus ?? details?.orderStatus);
  if (respCode === '00' && errorCode === '0' && orderStatus === 3) {
    return t?.checkout_cib_transaction_rejected || 'Transaction refusée';
  }
  if (details?.params && Object.keys(details.params).length > 0) {
    return details.params.respCode_desc || details.params.respCode_desc || 'Paiement refusé ou annulé';
  }
  return details.actionCodeDescription
    || details.action_code_description
    || details.ErrorMessage
    || details.errorMessage
    || 'Paiement refusé ou annulé';
}
